/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable unused-imports/no-unused-vars */
import React, { useEffect, useState } from 'react';

import Modal from '@/components/modal/modal';
import Typography from '@/components/typography/typography';
import Button from '@/components/button/button';

import {
	Box,
	CircularProgress,
	DialogActions,
	DialogContent,
	DialogProps,
	DialogTitle,
	IconButton,
} from '@mui/material';
import Close from '@mui/icons-material/Close';

import './template-name-modal.scss';
import { IEmail } from '../utils/types';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import DynamicInput from '@/components/form-control/form-builder/form-builder-element/text';
import { useForm } from 'react-hook-form';
import { patchV1TemplatesId, postV1Templates } from '@/api-client/api.system-feature/api';
import { getToken } from '@/utils/common';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

interface TemplateNameModalProps {
	isOpen: boolean;
	setIsOpen: (value: IEmail | null) => void;
	modalProps?: DialogProps;
	templateRoutes: Record<string, any>;
	payload: Record<string, any>;
	mode: 'add' | 'edit';
	templateData: Record<string, any> | null;
	onSaveSuccess?: () => void;
	onSaveError?: () => void;
	isSaving?: boolean;
}

const generateValidationSchema = () => {
	return yupResolver(
		Yup.object().shape({
			template_name: Yup.object().shape({
				name: Yup.string().trim().required('Template name is required'),
			})
		})
	)
}

const TemplateNameModal: React.FC<TemplateNameModalProps> = ({
	isOpen,
	setIsOpen,
	modalProps,
	templateRoutes,
	payload,
	mode = 'add',
	templateData,
	onSaveSuccess,
	onSaveError,
	isSaving: parentIsSaving = false,
}) => {
	const { handleSubmit, control, setValue } = useForm({ mode: 'all', resolver: generateValidationSchema() });
	const navigate = useNavigate();
	const { enqueueSnackbar } = useSnackbar();
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

	const handleModalClose = () => {
		if (!isSubmitting && !parentIsSaving) {
			setIsOpen(null);
		}
	};

	const onSubmit = async (values: any) => {
		setIsSubmitting(true);
		try {
			const { name = ''} = values?.template_name || {}
			payload.name = name
			const token = getToken();
			const finalPayload = { ...token, ...payload };
			const res = await (mode === 'edit' ? patchV1TemplatesId(finalPayload) : postV1Templates(finalPayload));
			if(res?.status_code === 200 || res?.status_code === 201 || res?.success) {
				enqueueSnackbar('Template saved successfully', { variant: 'success' });
				onSaveSuccess?.();
				navigate(templateRoutes.LIST);
			}
			setIsSubmitting(false);
		} catch (error) {
			const m = error?.message || 'Something went wrong'
			setIsSubmitting(false);
			enqueueSnackbar(m, { variant: 'error' });
			onSaveError?.();
		}
	}

	useEffect(() => {
		if(templateData && templateData?.name) {
			setValue('template_name.name', templateData?.name)
		}
	}, [setValue, templateData])

	return (
		<Modal
			open={isOpen}
			onClose={handleModalClose}
			maxWidth='xs'
			fullWidth
			{...modalProps}>
			<DialogTitle className='companySelectionModal--DialogTitle'>
				<Typography type='s3' color='inherit' weight='medium'>
					{parentIsSaving ? 'Saving Template...' : 'Template Name'}
				</Typography>
				<IconButton onClick={handleModalClose} disabled={isSubmitting || parentIsSaving}>
					<Close fontSize='small' />
				</IconButton>
			</DialogTitle>
			<DialogContent className='companySelectionModal--DialogContents'>
				<form onSubmit={handleSubmit(onSubmit)} id='template_name'>
					<DynamicInput 
						label={'Name'}
						name='name'
						placeholder={'Enter Name'}
						fieldArrayName='template_name'
						formControl={control}
						required
					/>
				</form>
			</DialogContent>
			<DialogActions className='companySelectionModal--DialogAction'>
				<Box display='flex' gap={1.5}>
					<Button className='companySelectionModal--DialogAction--WhiteBtn' onClick={handleModalClose} disabled={isSubmitting || parentIsSaving}>Cancel</Button>
					<Button type='submit' form='template_name' disabled={isSubmitting || parentIsSaving} endIcon={(isSubmitting || parentIsSaving) && <CircularProgress size={15}/>}>
						{parentIsSaving ? 'Saving...' : 'Save'}
					</Button>
				</Box>
			</DialogActions>
		</Modal>
	);
};

export default TemplateNameModal;
